import * as React from 'react';
import './styles.css';
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import ScaleLine from 'ol/control/ScaleLine';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import ImageWMS from 'ol/source/ImageWMS';
import ForestDataMapPopover from './ForestDataMapPopover';
import ForestDataMapLayerTree from './ForestDataMapLayerTree';
import ForestDataMapReset from './MapButtons/ForestDataMapReset';
import ForestDataMapZoomNotice from './MapButtons/ForestDataMapZoomNotice';

import {
  getMinMax,
  getSLDForLayer,
  createArcGISVectorSource,
  attachArcGISOverview,
  buildArcGISStyle,
  createArcGISTileLayer,
  setBoundaryColor,
} from './utils';
import DragDropSupport from './DragDropSupport';
import { UserContext } from '../context/UserContext';
import ForestDataMapLayer from './ForestDataMapLayer';
import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import { get as getProjection } from 'ol/proj';
import { LayerStyleHelper } from '../util/StyleHelper';
// import ForestDataITSTable from './ForestDataMapITSTable';

export default class ForestDataMap extends React.Component {
  static contextType = UserContext;
  constructor(props) {
    super(props);
    this.mapDivId = `map-${Math.random()}`;

    this.state = {
      queryLayerInfo: [],
      checkedQuerysets: {},
      queryLayers: [],
      reminder_visible: false,
      loading_datasets: {},
      filter_options: {},
      categories: [
        // "Watershed & Habitat Improvement",
        'Tree Planting',
        'Timber Harvest',
        'Sanitation & Salvage',
        'Mechanical and Hand Fuels Reduction',
        'Grazing',
        'Beneficial Fire',
        'Land Protection',
        'Not Defined',
        'Not Reported',
      ],
      owners: [
        'Federal',
        'Local',
        'NGO',
        'Private Industry',
        'Private Non-Industry',
        'State',
        'Tribal',
        'Not Reported',
      ],
      agencies: [
        'CA Environmental Protection Agency',
        'CA State Transportation Agency',
        'CA Natural Resources Agency',
        'Department of Defense',
        'Department of the Interior',
        'Department of Agriculture',
        'CA Air Resources Board',
        'Timber Companies',
        'Other',
        'Not Reported',
      ],
    };

    this.setCheckedQuerysets = this.setCheckedQuerysets.bind(this);
    this.setQueryLayers = this.setQueryLayers.bind(this);
    this.setQueryLayerInfo = this.setQueryLayerInfo.bind(this);
    this.getQueryData = this.getQueryData.bind(this);
    this.addLayerByUser = this.addLayerByUser.bind(this);
    this.setCategories = this.setCategories.bind(this);
    this.setAgencies = this.setAgencies.bind(this);
    this.setOwners = this.setOwners.bind(this);
    this.setReminderVisible = this.setReminderVisible.bind(this);
    this.setLoadingDatasets = this.setLoadingDatasets.bind(this);
    this.setFilterOptions = this.setFilterOptions.bind(this);

    // this is a backup base map layer for Google Terrain Map
    /*
        this.backgroundLayer = new TileLayer({
            source: new Stamen({
                crossOrigin: "anonymous",
                layer: 'terrain'
            })
        });
        */

    this.map = new OlMap({});

    this.map.on('loadstart', function () {
      document.getElementById('map').classList.add('spinner');
    });

    this.map.on('loadend', function () {
      document.getElementById('map').classList.remove('spinner');
    });

    this.map.addControl(
      new ScaleLine({
        units: 'us',
      })
    );
  }

  async setCategories(categories) {
    this.setState({ categories: categories });
  }

  async setAgencies(agencies) {
    this.setState({ agencies: agencies });
  }

  async setOwners(owners) {
    this.setState({ owners: owners });
  }

  async setReminderVisible(value) {
    this.setState({ reminder_visible: value });
  }

  componentDidMount() {
    this.backgroundLayer = new TileLayer({
      source: new XYZ({
        crossOrigin: 'anonymous',
        // Saved for future map changes
        // url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}',
        // attributions: 'Tiles © Esri — Source: USGS, Esri, TANA, DeLorme, NPS',
        // url: 'http://mt0.google.com/vt/lyrs=p&hl=en&x={x}&y={y}&z={z}',
        url: 'https://api.maptiler.com/maps/positron/{z}/{x}/{y}.png?key=8IPOL0tHId7YCUvo6p9h',
        maxZoom: 20,
        // attributions: '© OpenMapTiles © OpenStreetMap contributors',
      }),
    });
    // this.labelLayer = new TileLayer({
    //   source: new XYZ({
    //     crossOrigin: 'anonymous',
    //     url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Reference_Overlay/MapServer/tile/{z}/{y}/{x}',
    //   }),
    //   zIndex: 10,
    // });
    this.layers = [];
    for (var i = 0; i < this.props.selectedDatasets.length; i++) {
      let item = this.props.selectedDatasets[i];
      let gis_service = this.props.getGisService(item);
      let key = this.props.getItemKey(item);
      if (item.data_type === 'raster') {
        let min = item.dataset_metadata.filter((e) => e.name === 'min_value')[0]
          .float_value;
        let max = item.dataset_metadata.filter((e) => e.name === 'max_value')[0]
          .float_value;

        this.layers.push(
          new ImageLayer({
            visible: false,
            opacity: this.props.opacities[key] ?? 0.7,
            type: 'WMS',
            source: new ImageWMS({
              url: gis_service.service_url,
              crossOrigin: 'anonymous',
              params: {
                LAYER_NAME: gis_service.layer_name,
                LAYERS: gis_service.layer_name,
                STYLES: undefined,
                SLD_BODY: getSLDForLayer(
                  item.gis_services[0].layer_name,
                  gis_service.color_map.color_map_entries.length > 0
                    ? gis_service.color_map.color_map_entries[0].color
                    : '#D6B2FF',
                  gis_service.color_map.color_map_entries.length > 1
                    ? gis_service.color_map.color_map_entries[1].color
                    : '#3B0875',
                  max,
                  min,
                  max,
                  min
                ),
                TILED: true,
              },
              serverType: 'geoserver',
            }),
          })
        );
      } else {
        let service_info = this.props.getGisService(item);
        if (service_info.service_type !== 'ArcGIS') {
          this.layers.push(
            new ImageLayer({
              visible: true, //i==0,
              opacity: 1,
              type: 'WMS',
              zIndex: 100 - i,
              // type: 'WMSTime',
              // timeFormat: 'YYYY-MM-DD',
              // roundToFullHours: false,
              source: new ImageWMS({
                url: service_info.service_url,
                crossOrigin: 'anonymous',
                params: {
                  LAYERS: service_info.layer_name,
                  TILED: true,
                  // 'cql_filter': item.cql_filter
                },
              }),
            })
          );
        }
      }
    }
    proj4.defs(
      'EPSG:3310',
      '+proj=aea +lat_0=0 +lon_0=-120 +lat_1=34 +lat_2=40.5 +x_0=0 +y_0=-4000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs'
    );

    register(proj4);

    const projection = getProjection('EPSG:3310');

    this.map.setView(
      new OlView({
        projection: projection,
        center: [-200000, 0],
        zoom: 7,
      })
    );
    this.map.setTarget(this.mapDivId);
    this.setState({
      valueRanges: getMinMax(),
    });

    // load data from indexedDB
    let openRequest = indexedDB.open('WFR_DB', 1);
    openRequest.onsuccess = function () {
      let db = openRequest.result;
      let transaction = db.transaction('data', 'readwrite');
      let objectStore = transaction.objectStore('data');
      let request = objectStore.getAll();
      request.onerror = function () {};

      request.onsuccess = function () {
        if (request.result) {
          for (let i = 0; i < request.result.length; i++) {
            let fileContent = request.result[i];
            fileContent.definition = JSON.parse(fileContent.definition);
          }
        }
      };
    };

    this.setReminderVisible(true);
  }

  componentDidUpdate() {
    this.map.setLayers([
      this.backgroundLayer,
      ...this.layers,
      ...this.props.layersByUser,
      ...this.props.clipLayer,
    ]);

    document.getElementById('map_container').style.display = 'Block';

    for (var i = 0; i < this.props.selectedDatasets.length; i++) {
      let item = this.props.selectedDatasets[i];
      let gis_services = this.props.getGisService(item);
      let found = false;
      for (var j = 0; j < this.layers.length; j++) {
        if (
          gis_services.service_url ===
            this.layers[j].getProperties().service_url &&
          gis_services.layer_name === this.layers[j].getProperties().layer_name
        ) {
          found = true;
          break;
        }
      }

      let itemKey = this.props.getItemKey(item);

      if (!found) {
        let newLoading = { ...this.state.loading_datasets };
        newLoading[itemKey] = true;
        const setLoading = async (newLoading) => {
          await this.setLoadingDatasets(newLoading);
        };

        let is_visible =
          itemKey in this.props.checkedDatasets
            ? this.props.checkedDatasets[itemKey]
            : true;
        setLoading(newLoading);

        let layer = null;
        if (item.type) {
          if (gis_services.service_type === 'ArcGISTile') {
            layer = createArcGISTileLayer(gis_services, {
              visible: is_visible,
              opacity: this.props.opacities[itemKey] ?? 0.7,
            });
          } else if (gis_services.service_type === 'ArcGIS') {
            layer = new VectorLayer({
              visible: is_visible,
              opacity: this.props.opacities[itemKey] ?? 0.7,
              layer_name: gis_services.layer_name,
              service_url: gis_services.service_url,
              source: createArcGISVectorSource(gis_services.service_url),
              style: buildArcGISStyle(
                gis_services.color_map,
                this.props.layerColors?.[itemKey]
              ),
            });
            attachArcGISOverview(layer, gis_services.color_map);
          } else if (gis_services.layer_name.search('boundary') !== -1) {
            // New - boundaries
            layer = new ImageLayer({
              visible: is_visible,
              opacity: 1,
              type: 'WMS',
              layer_name: gis_services.layer_name,
              service_url: gis_services.service_url,
              source: new ImageWMS({
                url: gis_services.service_url,
                params: {
                  LAYER_NAME: gis_services.layer_name,
                  LAYERS: gis_services.layer_name,
                  STYLES: undefined,
                  FORMAT: 'image/png',
                  TRANSPARENT: true,
                  maxZoom: 20,
                  VERSION: '1.3.0',
                },
                ratio: 1,
                serverType: 'geoserver',
                crossOrigin: 'anonymous',
              }),
            });
            // The map rebuilds its layers when you come back to it (e.g.
            // after adding a dataset), so re-apply a color picked earlier.
            const savedColor = this.props.filterItems[itemKey]?.color?.[0];
            if (savedColor) {
              setBoundaryColor(
                layer.getSource(),
                gis_services.layer_name,
                savedColor
              );
            }
          } else {
            // New - clm
            if (gis_services.layer_name.search('rrk:') !== -1) {
              const sld = LayerStyleHelper(gis_services.layer_name);
              let sldName = null;
              let layerName = gis_services.layer_name;
              if (sld) {
                if (sld.includes('_std')) {
                  sldName = sld;
                } else {
                  layerName = sld;
                }
              }
              const params = {
                LAYER_NAME: gis_services.layer_name,
                LAYERS: layerName,
                STYLES: sldName,
                FORMAT: 'image/png',
                TRANSPARENT: true,
              };

              layer = new ImageLayer({
                visible: is_visible,
                opacity: 0.6,
                type: 'WMS',
                layer_name: gis_services.layer_name,
                service_url: gis_services.service_url,
                source: new ImageWMS({
                  url: gis_services.service_url,
                  crossOrigin: 'anonymous',
                  params: params,
                  serverType: 'geoserver',
                }),
              });
            }
            // New - its & operational layers
            else {
              layer = new ImageLayer({
                visible: is_visible,
                opacity: this.props.opacities[itemKey] ?? 0.6,
                type: 'WMS',
                layer_name: gis_services.layer_name,
                service_url: gis_services.service_url,
                source: new ImageWMS({
                  url: gis_services.service_url,
                  crossOrigin: 'anonymous',
                  params: {
                    LAYER_NAME: gis_services.layer_name,
                    LAYERS: gis_services.layer_name,
                    STYLES: undefined,
                    TILED: true,
                  },
                  serverType: 'geoserver',
                }),
              });
            }
          }
        } else if (
          item.data_type === 'raster' ||
          (item.data_type === 'vector' &&
            item.gis_services.filter((e) => e.service_type === 'ArcGIS')
              .length === 0)
        ) {
          // Old CLM
          layer = new ImageLayer({
            visible: is_visible,
            opacity: this.props.opacities[itemKey] ?? 0.7,
            type: 'WMS',
            layer_name: gis_services.layer_name,
            service_url: gis_services.service_url,
            source: new ImageWMS({
              url: gis_services.service_url,
              crossOrigin: 'anonymous',
              params: {
                LAYER_NAME: gis_services.layer_name,
                LAYERS: gis_services.layer_name,
                STYLES: undefined,
                TILED: true,
              },
              serverType: 'geoserver',
            }),
          });
        } else {
          // Old CLM - ArcGIS-backed (legacy gis_services)
          const gis_service = this.props.getGisService(item);
          layer = new VectorLayer({
            visible: is_visible,
            opacity: this.props.opacities[itemKey] ?? 0.7,
            layer_name: gis_service.layer_name,
            service_url: gis_service.service_url,
            source: createArcGISVectorSource(gis_service.service_url),
            style: buildArcGISStyle(gis_service.color_map),
          });
          attachArcGISOverview(layer, gis_service.color_map);
        }

        this.map.addLayer(layer);
        this.layers.push(layer);

        if (itemKey in this.props.checkedDatasets === false) {
          this.props.setCheckedDatasets((prev) => ({
            ...prev,
            [layer.getProperties().service_url +
            '/' +
            layer.getProperties().layer_name]: true,
          }));
        }

        newLoading = { ...this.state.loading_datasets };
        newLoading[itemKey] = false;

        setLoading(newLoading);
      }
    }
    for (var index = 0; index < this.props.layersByUser?.length; index++) {
      let item = this.props.layersByUser[index];
      let itemKey = this.props.getItemKey(item);
      let found = false;
      let is_visible =
        itemKey in this.props.checkedDatasets
          ? this.props.checkedDatasets[itemKey]
          : true;
      this.map.getLayers().forEach((layer) => {
        if (layer.getProperties().name === item.get('name')) {
          layer.setVisible(is_visible);
          found = true;
          return;
        }
      });

      if (!found) {
        console.log('not found');
        let newLoading = { ...this.state.loading_datasets };
        newLoading[itemKey] = true;
        const setLoading = async (newLoading) => {
          await this.setLoadingDatasets(newLoading);
        };

        setLoading(newLoading);

        let layer = item;

        layer.setVisible(is_visible);

        this.map.addLayer(layer);
        if (itemKey in this.props.checkedDatasets === false) {
          this.props.setCheckedDatasets((prev) => ({
            ...prev,
            [item.get('name')]: true,
          }));
        }

        newLoading = { ...this.state.loading_datasets };
        newLoading[itemKey] = false;

        setLoading(newLoading);
      }
    }

    // this.map.getLayers().forEach(layer => {
    this.layers.forEach((layer) => {
      let found = false;

      let layer_layer_name = layer.getProperties().layer_name;
      let layer_serviceUrl = layer.getProperties().service_url;
      for (let i = 0; i < this.props.selectedDatasets.length; i++) {
        let item = this.props.selectedDatasets[i];
        let gis_services = this.props.getGisService(item);
        let url = gis_services.service_url;
        let layer_name = gis_services.layer_name;

        if (
          layer &&
          url === layer_serviceUrl &&
          layer_name === layer_layer_name
        ) {
          found = true;
          break;
        }
      }

      if (!found) {
        for (let i = 0; i < this.state.queryLayers.length; i++) {
          let itemLayerId =
            this.state.queryLayers[i].getProperties()['layerId'];

          if (
            layer &&
            layer.getProperties &&
            itemLayerId === layer.getProperties()['layerId']
          ) {
            found = true;
            break;
          }
        }
      }
      //where the removal occurs
      if (!found && layer) {
        let mapLayerFound;
        this.map.getLayers().forEach((mapLayer) => {
          if (
            mapLayer.getProperties().layer_name ===
            layer.getProperties().layer_name
          ) {
            mapLayerFound = mapLayer;
          }
        });
        if (mapLayerFound) {
          this.map.removeLayer(mapLayerFound);
        }
        this.map.removeLayer(layer);
      }
    });

    let tmp = [];
    for (let j = 0; j < this.layers.length; j++) {
      let layer = this.layers[j];
      let found = false;
      for (let i = 0; i < this.props.selectedDatasets.length; i++) {
        let item = this.props.selectedDatasets[i];
        let gis_services = this.props.getGisService(item);
        let url = gis_services.service_url;
        let layer_name = gis_services.layer_name;

        if (
          layer &&
          url === layer.getProperties().service_url &&
          layer_name === layer.getProperties().layer_name
        ) {
          found = true;
          break;
        }
      }

      if (!found) {
        // pass
      } else {
        tmp.push(layer);
      }
    }
    this.layers = tmp;

    // make sure layers are appropriately visible
    this.layers.forEach((e) => {
      let key =
        e.getProperties().service_url + '/' + e.getProperties().layer_name;
      if (
        key in this.props.checkedDatasets &&
        e.getVisible() !== this.props.checkedDatasets[key]
      ) {
        e.setVisible(this.props.checkedDatasets[key]);
      }
    });

    this.map.getLayers().forEach((e) => {
      if (e.getProperties().name) {
        let key = e.getProperties().name;
        if (key in this.props.checkedDatasets) {
          e.setVisible(this.props.checkedDatasets[key]);
        }
        if (key in this.props.opacities) {
          e.setOpacity(this.props.opacities[key]);
        }
      } else {
        let key =
          e.getProperties().service_url + '/' + e.getProperties().layer_name;
        if (key in this.props.checkedDatasets) {
          e.setVisible(this.props.checkedDatasets[key]);
        }
        if (key in this.props.opacities) {
          e.setOpacity(this.props.opacities[key]);
        }
      }
    });
  }

  setFilterOptions(options) {
    this.setState({ filter_options: options });
  }

  setCheckedQuerysets(checkedQuerysets) {
    this.setState({ checkedQuerysets });
  }

  setQueryLayers(queryLayers) {
    this.setState({ queryLayers: queryLayers });
  }

  setQueryLayerInfo(queryLayerInfo) {
    this.setState(queryLayerInfo);
  }

  setLoadingDatasets(loading_datasets) {
    this.setState({ loading_datasets: loading_datasets });
  }

  getQueryData() {
    let datasetInfo = {};
    this.props.selectedDatasets.forEach((layer) => {
      let key = this.props.getItemKey(layer);
      if (
        layer &&
        this.props.checkedDatasets[key] !== undefined &&
        this.props.checkedDatasets[key]
      ) {
        if (this.props.filterItems[key] !== undefined) {
          datasetInfo[key] = this.props.filterItems[key];
        }
      }
    });

    return datasetInfo;
  }

  addLayerByUser = (layer) => {
    let newLayersByUser = [...this.props.layersByUser];
    newLayersByUser.push(layer);
    this.props.setLayersByUser(newLayersByUser);
    let newCheckedValues = { ...this.props.checkedDatasets };
    newCheckedValues[layer.getProperties().name] = true;
    this.props.setCheckedDatasets(newCheckedValues);
  };

  render() {
    return (
      <div
        style={{
          display: 'block',
        }}
      >
        <div
          id="map_container"
          style={{ height: 'calc(100vh )', position: 'relative' }}
        >
          <div
            id={this.mapDivId}
            style={{ height: '100%', marginRight: '350px' }}
          />
          <ForestDataMapPopover
            map={this.map}
            layers={this.layers}
            valueRanges={this.props.valueRanges}
            selectedDatasets={this.props.selectedDatasets}
            getGisService={this.props.getGisService}
            getItemKey={this.props.getItemKey}
          />
          {/* <ForestDataMapQueryTool map={this.map} layers={this.layers}
                                        queryLayers={this.state.queryLayers}
                                        setQueryLayers={this.setQueryLayers}
                                        setQueryLayerInfo={this.setQueryLayerInfo}
                                        getQueryData={this.getQueryData}   
                                        queryLayerInfo={this.state.queryLayerInfo}
                                        checkedQuerysets={this.state.checkedQuerysets}                                  
                                        setCheckedQuerysets={this.setCheckedQuerysets}    
                                        access_token={this.context.user.access_token}                               
                />     */}
          {/* <ForestDataMapIntersect map={this.map} layers={this.layers}
                                        queryLayers={this.state.queryLayers}
                                        setQueryLayers={this.setQueryLayers}
                                        setQueryLayerInfo={this.setQueryLayerInfo}
                                        getQueryData={this.getQueryData}   
                                        queryLayerInfo={this.state.queryLayerInfo}
                                        checkedQuerysets={this.state.checkedQuerysets}                                  
                                        setCheckedQuerysets={this.setCheckedQuerysets}  /> */}

          {/* <ForestDataMapDownload map={this.map} layers={this.layers} 
                                       checkedDatasets={this.props.checkedDatasets}
                                       selectedDatasets={this.props.selectedDatasets}
                                       valueRanges={this.props.valueRanges}
                                       filterItems={this.props.filterItems}  
                                       getQueryData={this.getQueryData}    
                                       access_token={this.context.user.access_token}  
                                       /> */}
          <ForestDataMapReset map={this.map} layers={this.layers} />
          <ForestDataMapZoomNotice map={this.map} />

          <ForestDataMapLayer map={this.map} layers={this.layers} />
          {/* <ForestDataITSTable map={this.map} layers={this.layers} /> */}
          <ForestDataMapLayerTree
            map={this.map}
            layers={this.layers}
            layersByUser={this.props.layersByUser}
            setLayersByUser={this.props.setLayersByUser}
            setValueRanges={this.props.setValueRanges}
            setQueryLayers={this.setQueryLayers}
            filter_options={this.state.filter_options}
            setFilterOptions={this.setFilterOptions}
            queryLayers={this.state.queryLayers}
            valueRanges={this.props.valueRanges}
            checkedDatasets={this.props.checkedDatasets}
            checkedQuerysets={this.state.checkedQuerysets}
            setCheckedQuerysets={this.setCheckedQuerysets}
            setCheckedDatasets={this.props.setCheckedDatasets}
            queryLayerInfo={this.state.queryLayerInfo}
            setQueryLayerInfo={this.setQueryLayerInfo}
            agencies={this.state.agencies}
            owners={this.state.owners}
            categories={this.state.categories}
            setAgencies={this.setAgencies}
            setCategories={this.setCategories}
            setOwners={this.setOwners}
            getGisService={this.props.getGisService}
            getItemKey={this.props.getItemKey}
            filtersDatasets={this.props.filtersDatasets}
            setFilterDatasets={this.props.setFilterDatasets}
            loading_datasets={this.state.loading_datasets}
            setLoadingDatasets={this.setLoadingDatasets}
            filterItems={this.props.filterItems}
            setFilterItems={this.props.setFilterItems}
            opacities={this.props.opacities}
            setOpacities={this.props.setOpacities}
            layerColors={this.props.layerColors}
            setLayerColors={this.props.setLayerColors}
            access_token={this.context.user.access_token}
            addLayerByUser={this.addLayerByUser}
            clipLayer={this.props.clipLayer}
            setClipLayer={this.props.setClipLayer}
            selectedClipBoundary={this.props.selectedClipBoundary}
            setSelectedClipBoundary={this.props.setSelectedClipBoundary}
            selectedClipSubset={this.props.selectedClipSubset}
            setSelectedClipSubset={this.props.setSelectedClipSubset}
            selectedCustomClipKey={this.props.selectedCustomClipKey}
            setSelectedCustomClipKey={this.props.setSelectedCustomClipKey}
            clipEnabled={this.props.clipEnabled}
            setClipEnabled={this.props.setClipEnabled}
            highlightClip={this.props.highlightClip}
            setHighlightClip={this.props.setHighlightClip}
          />
          {/* <ForestDataMapSaveWorkspace map={this.map} layer={this.layers}
                                        selectedDatasets={this.props.selectedDatasets}
                                        checkedDatasets={this.props.checkedDatasets}    
                                        access_token={this.context.user.access_token}  
                                        jwtToken={this.context.user.jwtToken} 
                                        valueRanges={this.props.valueRanges}    
                                        filterItems={this.props.filterItems}  
                                        getItemKey={this.props.getItemKey}
                                        opacities={this.props.opacities}
                                        setOpacities={this.props.setOpacities}                                                           
                /> */}
          <DragDropSupport
            map={this.map}
            addLayerByUser={this.addLayerByUser}
          />
        </div>
        {/* <Modal 
                title="Reminder"
                open={this.state.reminder_visible}
                onOk={() => this.setReminderVisible(false)}
                okText="I understand"
                cancelButtonProps={{
                   disabled: true ,
                  }}
                style={{
                    minWidth:'100px',
                    zIndex: -1
                }}
                >                            
                <div>
                    This is a prototype and data available here is only provided for the purpose of gathering user feedback. Please acknowledge that you understand and will not use this data. 
                </div>
            </Modal> */}
      </div>
    );
  }
}
